import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Btn, Field, Input, Select, Textarea, Modal, DataTable, Toolbar, exportCsv, Plus, toast } from "@/pages/app/kit";
import { StatusBadge, DemoBadge } from "@/components/shared";

const EMPTY = {
  product: "", grade: "Fresh Standard", quantity: 0, unit: "kg", delivery_date: "",
  max_price: 0, target_price: 0, packaging: "", preparation: "fresh", delivery_location: "",
  region: "lubelskie", accepts_substitutes: false, notes: "",
};

export default function DemandPage() {
  const { t } = useT();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const canCreate = ["buyer", "coordinator", "admin", "super_admin"].includes(user.role);

  const load = () => api.get("/demand").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = { ...form };
      ["quantity", "max_price", "target_price"].forEach((k) => p[k] = parseFloat(p[k]) || 0);
      await api.post("/demand", p);
      toast.success(t("cta.create"));
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filtered = rows.filter((r) => r.product.toLowerCase().includes(search.toLowerCase()));
  const columns = [
    { key: "product", label: t("crop.product"), render: (r) => <div><span className="font-semibold text-charcoal">{r.product}</span>{r.is_demo && <DemoBadge className="ml-2" />}<div className="text-xs text-mutedtext">{r.owner_name}</div></div> },
    { key: "grade", label: t("demand.grade") },
    { key: "quantity", label: t("crop.quantity"), render: (r) => `${r.quantity?.toLocaleString()} ${r.unit}` },
    { key: "matched_quantity", label: t("crop.matched"), render: (r) => `${(r.matched_quantity||0).toLocaleString()} ${r.unit}` },
    { key: "target_price", label: t("demand.targetPrice"), render: (r) => `${r.target_price} PLN` },
    { key: "delivery_date", label: t("demand.deliveryDate"), render: (r) => r.delivery_date?.slice(0,10) },
    { key: "status", label: t("crop.status"), render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageTitle title={t("dashboard.myDemand")} subtitle={t("demand.title")}
        action={canCreate && <Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-demand-btn">{t("cta.registerDemand")}</Btn>} />
      <Toolbar search={search} setSearch={setSearch} onExport={() => exportCsv("demand.csv", filtered)} />
      <DataTable columns={columns} rows={filtered} empty={t("empty")} testid="demand-table" />

      <Modal open={open} onClose={() => setOpen(false)} title={t("demand.title")} wide>
        <form onSubmit={submit} className="space-y-4" data-testid="demand-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("crop.product")} required><Input data-testid="demand-product" value={form.product} onChange={set("product")} required /></Field>
            <Field label={t("demand.grade")}><Select value={form.grade} onChange={set("grade")}>
              {["Fresh Premium","Fresh Standard","Cosmetically Imperfect but Edible","Processing Grade"].map((g)=><option key={g}>{g}</option>)}
            </Select></Field>
            <Field label={t("crop.quantity")} required><Input data-testid="demand-qty" type="number" value={form.quantity} onChange={set("quantity")} /></Field>
            <Field label={t("crop.unit")}><Select value={form.unit} onChange={set("unit")}><option>kg</option><option>t</option></Select></Field>
            <Field label={t("demand.targetPrice")}><Input type="number" step="0.01" value={form.target_price} onChange={set("target_price")} /></Field>
            <Field label={t("demand.maxPrice")}><Input type="number" step="0.01" value={form.max_price} onChange={set("max_price")} /></Field>
            <Field label={t("demand.deliveryDate")}><Input type="date" value={form.delivery_date?.slice(0,10)} onChange={set("delivery_date")} /></Field>
            <Field label={t("demand.preparation")}><Select value={form.preparation} onChange={set("preparation")}>
              {["fresh","peeled","cut","frozen"].map((g)=><option key={g}>{g}</option>)}
            </Select></Field>
            <Field label={t("demand.location")}><Input value={form.delivery_location} onChange={set("delivery_location")} placeholder="Lublin" /></Field>
            <Field label={t("crop.region")}><Input value={form.region} onChange={set("region")} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.accepts_substitutes} onChange={set("accepts_substitutes")} /> Accepts substitutes</label>
          <Field label={t("crop.notes")}><Textarea rows={2} value={form.notes} onChange={set("notes")} /></Field>
          <div className="flex gap-3"><Btn type="submit" disabled={busy} data-testid="demand-submit">{busy ? "…" : t("cta.create")}</Btn><Btn type="button" variant="ghost" onClick={() => setOpen(false)}>{t("cta.cancel")}</Btn></div>
        </form>
      </Modal>
    </div>
  );
}
